<script setup lang="ts">
// McpLayout (ORP-21466) — the shell for `/mcp/*`. Same boot contract as `AppLayout` (module 07
// owns the boot, the layout only renders its state), same `:key="workspaceId"` remount so a
// workspace switch refreshes the open screen — and none of the web chrome: no topbar, no nav
// registry, no dashboard modal host.
//
// The zero-workspace branch AppLayout carries is deliberately absent: `resolvePermissionAccess`
// sends a zero-workspace user to onboarding BEFORE its MCP branch runs, so an empty state here
// would be unreachable code pretending to be a safety net.
import { computed, ref } from 'vue'

import AppSplash from '../shell/components/AppSplash.vue'
import BootErrorState from '../shell/components/BootErrorState.vue'
import McpSidebar from '../shell/components/mcp/McpSidebar.vue'
import { useBootstrap } from '../shell/useBootstrap'

import { useWorkspace } from '@/features/account'

const bootstrap = useBootstrap()
const { workspaceId } = useWorkspace()
const retrying = ref(false)

const routeKey = computed(() => String(workspaceId.value ?? 'no-workspace'))

async function retry(): Promise<void> {
  retrying.value = true
  try {
    await bootstrap.retry()
  } finally {
    retrying.value = false
  }
}
</script>

<template>
  <AppSplash
    v-if="
      bootstrap.status.value === 'idle' ||
      bootstrap.status.value === 'booting' ||
      (bootstrap.status.value === 'ready' && !bootstrap.isRouteReady.value)
    "
  />
  <BootErrorState
    v-else-if="bootstrap.status.value === 'error'"
    :retrying="retrying"
    @retry="retry"
  />
  <div v-else class="mcp-layout">
    <McpSidebar />
    <main class="mcp-layout__content">
      <div :key="routeKey" class="mcp-layout__route">
        <slot />
      </div>
    </main>
  </div>
</template>

<style scoped>
.mcp-layout {
  display: grid;
  min-height: 100dvh;
  grid-template-columns: max-content minmax(0, 1fr);
  background: var(--ui-color-bg);
}
.mcp-layout__content {
  min-width: 0;
  padding: var(--ui-space-xl) 2.25rem;
}
.mcp-layout__route {
  min-width: 0;
}
@media (max-width: 56rem) {
  .mcp-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .mcp-layout__content {
    padding: var(--ui-space-md);
  }
}
</style>
